import React from 'react';

// Define the card item interface
interface ConnectCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  borderColor: string;
  bgColor: string;
}

interface ConnectCardsProps {
  initialCards?: ConnectCard[];
}

// Simple Card component without drag functionality
const CardItem = ({ card, index }: { card: ConnectCard; index: number }) => {
  return (
    <div
      className={`${card.bgColor} p-3 rounded-lg border ${card.borderColor} transition-all duration-200 mb-4 relative`}
    >
      <div className="flex items-start">
        <div
          className={`w-8 h-8 rounded-full ${card.bgColor} flex items-center justify-center ${card.color} mr-3`}
        >
          {card.icon}
        </div>
        <div className="flex-1">
          <p className="text-slate-800 text-sm font-medium">
            {index + 1}. {card.title}
          </p>
          <p className="text-slate-600 text-xs mt-1">{card.description}</p>
          <p className={`${card.color} text-xs mt-2 font-medium`}>
            {card.id === 'savings-account' && 'Potential benefit: +$320/year'}
            {card.id === 'debt-consolidation' && 'Potential savings: $150/month'}
            {card.id === 'financial-planning' && 'Next available: May 15, 2023'}
            {card.id === 'credit-builder' && 'Estimated improvement: +30 points'}
            {card.id === 'auto-loan' && 'Rate as low as 3.49% APR'}
            {card.id === 'retirement-planning' && 'Free consultation available'}
          </p>
        </div>
      </div>
    </div>
  );
};

const defaultCards: ConnectCard[] = [
  {
    id: 'savings-account',
    title: 'Savings Account Upgrade',
    description: 'Higher interest rate based on current balance',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
          clipRule="evenodd"
        />
      </svg>
    ),
    color: 'text-brand-blue',
    borderColor: 'border-brand-blue/20',
    bgColor: 'bg-brand-blue/5',
  },
  {
    id: 'debt-consolidation',
    title: 'Debt Consolidation Loan',
    description: 'Could reduce monthly payments by up to 15%',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
        <path
          fillRule="evenodd"
          d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
          clipRule="evenodd"
        />
      </svg>
    ),
    color: 'text-brand-purple',
    borderColor: 'border-brand-purple/20',
    bgColor: 'bg-brand-purple/5',
  },
  {
    id: 'financial-planning',
    title: 'Financial Planning Session',
    description: 'Free consultation with retirement specialist',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
          clipRule="evenodd"
        />
      </svg>
    ),
    color: 'text-brand-blue',
    borderColor: 'border-brand-blue/20',
    bgColor: 'bg-brand-blue/5',
  },
  {
    id: 'credit-builder',
    title: 'Credit Builder Program',
    description: 'Improve your credit score with our guided program',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
          clipRule="evenodd"
        />
      </svg>
    ),
    color: 'text-green-600',
    borderColor: 'border-green-200',
    bgColor: 'bg-green-50',
  },
  {
    id: 'auto-loan',
    title: 'Auto Loan Pre-Approval',
    description: 'Get pre-approved for your next vehicle purchase',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
        <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-1h3a1 1 0 001-1v-3a1 1 0 00-.293-.707l-2-2A1 1 0 0012 7h-1V5a1 1 0 00-1-1H3z" />
      </svg>
    ),
    color: 'text-brand-link',
    borderColor: 'border-brand-link/20',
    bgColor: 'bg-brand-link/5',
  },
  {
    id: 'retirement-planning',
    title: 'Retirement Planning',
    description: 'Start planning for your future today',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
      </svg>
    ),
    color: 'text-yellow-600',
    borderColor: 'border-yellow-200',
    bgColor: 'bg-yellow-50',
  },
];

const ConnectCardsDndKitInline: React.FC<ConnectCardsProps> = ({
  initialCards = defaultCards,
}) => {
  // Use the initial cards directly without state management for reordering
  const cards = initialCards;

  return (
    <>
      {cards.map((card, index) => (
        <CardItem key={card.id} card={card} index={index} />
      ))}
    </>
  );
};

export default ConnectCardsDndKitInline;
